<template>
    <div class="row">

        <div style="width:380px">

            <fieldset>
                <legend>坐标输入</legend>
                <div class="param-row">
                    <label>P1:</label>
                    X<NumberInput v-model="x1" style="width:80px" @paste-pair="([x, y]) => { x1 = x; y1 = y; calc() }" @change="calc" @enter="nextNumber(0)" data-nav="0" />
                    Y<NumberInput v-model="y1" style="width:80px" @paste-pair="([x, y]) => { x1 = x; y1 = y; calc() }" @change="calc" @enter="nextNumber(1)" data-nav="1" />
                </div>
                <div class="param-row">
                    <label>P2:</label>
                    X<NumberInput v-model="x2" style="width:80px" @paste-pair="([x, y]) => { x2 = x; y2 = y; calc() }" @change="calc" @enter="nextNumber(2)" data-nav="2" />
                    Y<NumberInput v-model="y2" style="width:80px" @paste-pair="([x, y]) => { x2 = x; y2 = y; calc() }" @change="calc" @enter="nextNumber(3)" data-nav="3" />
                </div>
            </fieldset>

            <fieldset>
                <legend>测量结果</legend>
                <div v-for="(item, i) in results" :key="i" class="param-row result-row">
                    <label>{{ item.label }}</label>
                    <span class="result-value">{{ item.display }}</span>
                    <button class="icon-btn copy-btn" :class="{copied: copiedIdx === i}" :title="'复制 ' + item.label" @click="copyValue(item.copy, i)">
                        {{ copiedIdx === i ? '✓' : '📋' }}
                    </button>
                </div>
            </fieldset>

            <fieldset>
                <legend>操作</legend>
                <button @click="calc">计算</button>
            </fieldset>

        </div>

        <div class="grow" style="text-align: center;">
            <canvas ref="canvas" :width="CanvasW" :height="CanvasH" style="width:601px;height:601px"></canvas>
        </div>

    </div>
</template>

<script>
import { ref, reactive } from "vue"
import NumberInput from "./NumberInput.vue"

const RAD2DEG = 180 / Math.PI

const CanvasW = 601
const CanvasH = 601

export default {
    components: { NumberInput },
    setup() {
        const x1 = ref(0)
        const y1 = ref(0)
        const x2 = ref(120)
        const y2 = ref(80)

        const results = reactive([])
        const copiedIdx = ref(-1)

        return {
            x1, y1, x2, y2,
            results, copiedIdx,
            RAD2DEG, CanvasW, CanvasH,
        }
    },
    mounted() {
        this.ctx = this.$refs.canvas.getContext("2d")
        this.calc()
    },
    methods: {
        calc() {
            const x1 = Number(this.x1) || 0
            const y1 = Number(this.y1) || 0
            const x2 = Number(this.x2) || 0
            const y2 = Number(this.y2) || 0

            const dx = x2 - x1
            const dy = y2 - y1
            const dist = Math.sqrt(dx * dx + dy * dy)
            const angleX = Math.atan2(dy, dx)       // 与 X 轴夹角 (rad)
            const angleY = Math.atan2(dx, dy)       // 与 Y 轴夹角 (rad)
            const mx = (x1 + x2) / 2
            const my = (y1 + y2) / 2

            // 归一化角度到 -90°~90°（线段无方向，170° 等价于 -10°）
            const normDeg = (deg) => {
                if (deg > 90) return deg - 180
                if (deg < -90) return deg + 180
                return deg
            }
            const degX = normDeg(angleX * RAD2DEG)
            const degY = normDeg(angleY * RAD2DEG)
            const radX = degX / RAD2DEG
            const radY = degY / RAD2DEG

            // 数值保留 3 位用于复制
            const f3 = (n) => Number(n.toFixed(3))

            const items = [
                { label: "距离",        display: dist.toFixed(3) + " mm",            copy: String(f3(dist)) },
                { label: "ΔX",          display: dx.toFixed(3) + " mm",              copy: String(f3(dx)) },
                { label: "ΔY",          display: dy.toFixed(3) + " mm",              copy: String(f3(dy)) },
                { label: "与X轴夹角",   display: degX.toFixed(3) + "°", copy: String(Number(degX.toFixed(3))) },
                { label: "与X轴夹角(弧度)", display: radX.toFixed(4) + " rad",      copy: String(Number(radX.toFixed(4))) },
                { label: "与Y轴夹角",   display: degY.toFixed(3) + "°", copy: String(Number(degY.toFixed(3))) },
                { label: "与Y轴夹角(弧度)", display: radY.toFixed(4) + " rad",      copy: String(Number(radY.toFixed(4))) },
                { label: "中点坐标",    display: `(${mx.toFixed(3)}, ${my.toFixed(3)})`, copy: `${f3(mx)},${f3(my)}` },
            ]

            this.results.splice(0, this.results.length, ...items)
            this.draw()
        },

        copyValue(text, idx) {
            const done = () => {
                this.copiedIdx = idx
                clearTimeout(this._copyTimer)
                this._copyTimer = setTimeout(() => {
                    if (this.copiedIdx === idx) this.copiedIdx = -1
                }, 1000)
            }
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(done, () => this._fallbackCopy(text, done))
            } else {
                this._fallbackCopy(text, done)
            }
        },
        _fallbackCopy(text, done) {
            const ta = document.createElement("textarea")
            ta.value = text
            ta.style.position = "fixed"
            ta.style.left = "-9999px"
            document.body.appendChild(ta)
            ta.select()
            try { document.execCommand("copy") } catch (e) { /* ignore */ }
            document.body.removeChild(ta)
            done && done()
        },

        draw() {
            const canvas = this.$refs.canvas
            if (!canvas) return
            // 重置 canvas（清屏 + 复位变换矩阵）
            canvas.width = canvas.width
            canvas.height = canvas.height

            const ctx = this.ctx

            // 坐标轴（原点居中于 canvas）
            this.drawAxies(ctx)

            // 平移到原点
            ctx.translate(CanvasW / 2, CanvasH / 2)

            const x1 = Number(this.x1) || 0
            const y1 = Number(this.y1) || 0
            const x2 = Number(this.x2) || 0
            const y2 = Number(this.y2) || 0

            // 自动适配缩放（留出边距）
            const max = Math.max(Math.abs(x1), Math.abs(y1), Math.abs(x2), Math.abs(y2), 1)
            const scale = 250 / max

            // 两点（canvas Y 朝下，数学 Y 朝上 → 取负）
            const p1x = x1 * scale, p1y = -y1 * scale
            const p2x = x2 * scale, p2y = -y2 * scale

            // 连线
            ctx.strokeStyle = "#000"
            ctx.lineWidth = 1.5
            ctx.beginPath()
            ctx.moveTo(p1x, p1y)
            ctx.lineTo(p2x, p2y)
            ctx.stroke()
            ctx.lineWidth = 1

            // 连线中点标注距离
            const dist = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2)
            ctx.fillStyle = "#0033aa"
            ctx.font = "12px sans-serif"
            ctx.textAlign = "center"
            ctx.fillText(`d=${dist.toFixed(3)}`, (p1x + p2x) / 2, (p1y + p2y) / 2 - 8)

            // 从原点画到各点的辅助线（虚线，便于看角度）
            ctx.strokeStyle = "#bbb"
            ctx.setLineDash([4, 4])
            ctx.beginPath()
            ctx.moveTo(0, 0); ctx.lineTo(p1x, p1y)
            ctx.moveTo(0, 0); ctx.lineTo(p2x, p2y)
            ctx.stroke()
            ctx.setLineDash([])

            // 点 P1 / P2
            this.drawPoint(ctx, p1x, p1y, "#cc0000", `P1(${x1.toFixed(3)}, ${y1.toFixed(3)})`, -14)
            this.drawPoint(ctx, p2x, p2y, "#cc0000", `P2(${x2.toFixed(3)}, ${y2.toFixed(3)})`, -14)
        },

        drawAxies(ctx) {
            ctx.lineWidth = 1
            ctx.strokeStyle = "#aaa"
            ctx.beginPath()
            ctx.moveTo(0, CanvasH / 2); ctx.lineTo(CanvasW, CanvasH / 2)   // X 轴
            ctx.moveTo(CanvasW / 2, 0); ctx.lineTo(CanvasW / 2, CanvasH)   // Y 轴
            ctx.stroke()
            // 轴标签
            ctx.fillStyle = "#888"
            ctx.font = "12px sans-serif"
            ctx.textAlign = "left"
            ctx.fillText("X+", CanvasW - 22, CanvasH / 2 - 6)
            ctx.fillText("Y+", CanvasW / 2 + 6, 14)
        },

        drawPoint(ctx, x, y, color, title, textOffsetY) {
            ctx.fillStyle = color
            ctx.beginPath()
            ctx.arc(x, y, 4, 0, 2 * Math.PI)
            ctx.fill()
            ctx.font = "12px sans-serif"
            ctx.textAlign = "center"
            ctx.fillStyle = "#000"
            ctx.fillText(title, x, y + textOffsetY)
        },

        nextNumber(idx) {
            const inputs = this.$el.querySelectorAll('[data-nav]')
            idx++
            if (idx >= inputs.length) idx = 0
            inputs[idx].focus()
        },
    },
}
</script>

<style scoped>
.result-row .result-value {
    flex: 1;
    text-align: left;
    font-size: 13px;
}
.copy-btn {
    font-size: 14px;
    padding: 2px 6px;
}
.copy-btn.copied {
    color: #1a7f37;
}
</style>
